import {
  getRecommendedServices,
  hasMultipleEmployees,
  showExtraTimeView,
} from '@/components/modules/modals/BookAppointment/BookAppointment.helpers';
import { getAnyEmployee } from '@/features/booking-selection/constants';
import { getPickedEmployee } from '@/helpers/employee';
import { getExtraTimeRequirementForUser } from '@/helpers/userHelper';
import { EXTRA_TIME_REQUIREMENT, ExtraTimeRequirement } from '@/types/state/book';
import { Place, PlaceEmployee, PlaceService } from '@/types/state/shared';
import { BOOKING_FLOW_STEP_SLUGS, BookingFlowStep } from './constants';
import type { BookingFlowGoToStepTarget } from './goToStep';

/**
 * The selection context the step conditions read — a projection of `state.book` plus the
 * user, so both step functions stay pure (R3): no redux, no router, no side effects.
 */
export type BookingFlowStepSelection = {
  /** The booking-flow place (`state.book.place`). */
  place: Place | undefined;
  /** Picked services — main services plus any picked add-ons (`state.book.services`). */
  services: PlaceService[] | undefined;
  /**
   * Picked employee id (`state.book.employee`). `ANY_EMPLOYEE_ID` (0) counts as picked;
   * only `null`/`undefined` mean "nothing picked" — which is why the conditions compare
   * with `!= null`, never truthiness.
   */
  employee: number | null | undefined;
  /** The in-flow extra-time answer (`state.book.extraTimeRequirement`), if any. */
  extraTimeRequirement?: ExtraTimeRequirement;
  /** `state.user` — fallback source for a previously stored per-place extra-time answer. */
  user?: unknown;
  /**
   * Whether the calendar carries an employee picker of its own (`useCalendarOffersEmployeePick`).
   * Where it does, the employee step is skipped and the pick is made on the calendar instead.
   */
  calendarOffersEmployeePick: boolean;
};

/**
 * The four steps in the only order they can appear. `extra-tid` stays last, diverging from
 * Figma (KTD5, OQ1 resolved): extra time is itself an add-on from the same pool `tillval`
 * lists, so asking it first would make one service reachable on two screens.
 */
export const BOOKING_FLOW_STEP_ORDER: readonly BookingFlowStep[] = [
  BOOKING_FLOW_STEP_SLUGS.SELECT_SERVICE,
  BOOKING_FLOW_STEP_SLUGS.SELECT_EMPLOYEE,
  BOOKING_FLOW_STEP_SLUGS.ADD_ONS,
  BOOKING_FLOW_STEP_SLUGS.EXTRA_TIME,
];

/**
 * Mirrors `SelectAddOns.hooks.tsx`: picked employee looked up on the place, falling back to
 * the any-employee stand-in — `getRecommendedServices` returns `[]` for `undefined`, so a raw
 * pass-through would make this gate disagree with the `/tillval` page.
 */
const resolvePickedEmployee = ({ employee, place }: BookingFlowStepSelection): PlaceEmployee =>
  (employee != null ? getPickedEmployee(employee, place?.employees ?? []) : undefined) ?? getAnyEmployee();

/** Mirrors `getScreenView`: the in-flow answer wins, else the user's stored per-place answer. */
const resolveExtraTimeRequirement = (selection: BookingFlowStepSelection): ExtraTimeRequirement =>
  selection.extraTimeRequirement || getExtraTimeRequirementForUser(selection.user, selection.place?.id);

type StepCondition = (selection: BookingFlowStepSelection) => boolean;

const STEP_CONDITIONS: Record<BookingFlowStep, StepCondition> = {
  // Employee-first entry: nothing to book has been picked yet.
  [BOOKING_FLOW_STEP_SLUGS.SELECT_SERVICE]: ({ services }) => (services ?? []).length === 0,

  // Mirrors getScreenView's pickEmployee condition (place set, employees not hidden, none picked,
  // more than one bookable), plus the chrome gate: the step exists only where the calendar has
  // no employee picker of its own.
  [BOOKING_FLOW_STEP_SLUGS.SELECT_EMPLOYEE]: ({ place, employee, calendarOffersEmployeePick }) =>
    !calendarOffersEmployeePick &&
    place != null &&
    !(place.about?.settings?.hideEmployees ?? false) &&
    employee == null &&
    hasMultipleEmployees(place),

  // KTD6 — both conjuncts of SelectAddOns.hooks.tsx's canShow, never the raw addOnServices
  // array, so this gate and the /tillval page agree by construction.
  [BOOKING_FLOW_STEP_SLUGS.ADD_ONS]: (selection) => {
    const services = selection.services ?? [];
    const pickedMainServices = services.filter((service) => !service?.about?.settings?.isAddOn);
    return (
      getRecommendedServices(services, resolvePickedEmployee(selection)).length > 0 && pickedMainServices.length <= 1
    );
  },

  [BOOKING_FLOW_STEP_SLUGS.EXTRA_TIME]: (selection) =>
    showExtraTimeView(
      selection.services ?? [],
      resolvePickedEmployee(selection),
      resolveExtraTimeRequirement(selection),
    ),
};

/**
 * The main service a calendar exit books — the last picked non-add-on, exactly as
 * `getRecommendedServices` defines the main service.
 */
const getMainService = (services: PlaceService[]): PlaceService | undefined =>
  services
    .slice()
    .reverse()
    .find((service) => !service?.about?.settings?.isAddOn);

const firstStepFrom = (startIndex: number, selection: BookingFlowStepSelection): BookingFlowGoToStepTarget => {
  for (const step of BOOKING_FLOW_STEP_ORDER.slice(startIndex)) {
    if (STEP_CONDITIONS[step](selection)) {
      return { type: 'step', step };
    }
  }
  const mainService = getMainService(selection.services ?? []);
  if (!mainService) {
    // No condition holds and nothing bookable is picked: send the visitor to the earliest
    // valid step (R9) rather than building a calendar URL for `undefined`.
    return { type: 'step', step: BOOKING_FLOW_STEP_SLUGS.SELECT_SERVICE };
  }
  return { type: 'calendar', mainService };
};

/**
 * The step a flow entry lands on: the first step whose condition holds for `selection`, or
 * the calendar exit when none does. One shared implementation for all three place-page
 * entry points (R3) — conditions are evaluated stepwise, since each depends on the previous
 * step's answer.
 */
export const getFirstStep = (selection: BookingFlowStepSelection): BookingFlowGoToStepTarget =>
  firstStepFrom(0, selection);

/**
 * The step that follows `currentStep` once it has been answered: `selection` must already
 * include the answer (the picked service, employee, add-ons or extra-time requirement). The
 * walk is forward-only — earlier steps are never revisited; unmet prerequisites behind
 * `currentStep` are the page guards' concern (R9), not this function's.
 */
export const getNextStep = (
  currentStep: BookingFlowStep,
  selection: BookingFlowStepSelection,
): BookingFlowGoToStepTarget => firstStepFrom(BOOKING_FLOW_STEP_ORDER.indexOf(currentStep) + 1, selection);

/**
 * Blocking prerequisites, as opposed to the optional steps (`tillval` / `extra-tid`), whose
 * "continued without answering" state is not in the URL — a guard re-running the full
 * `getFirstStep` walk over those would bounce the visitor back and loop forever.
 */
const PREREQUISITE_STEPS: readonly BookingFlowStep[] = [
  BOOKING_FLOW_STEP_SLUGS.SELECT_SERVICE,
  BOOKING_FLOW_STEP_SLUGS.SELECT_EMPLOYEE,
];

/**
 * `tillval`'s remain rule must not mirror its entry rule (R1): once landed, nothing the
 * visitor does should bounce them off the step.
 */
const REMAIN_CONDITIONS: Partial<Record<BookingFlowStep, StepCondition>> = {
  [BOOKING_FLOW_STEP_SLUGS.ADD_ONS]: (selection) =>
    (selection.services ?? []).filter((service) => !service?.about?.settings?.isAddOn).length >= 1,
  // Answered-but-askable stays renderable (2026-08-13, user decision): the answer persists
  // on the user, so an entry-rule guard would bounce every calendar-back rewind straight
  // back to the calendar (the R13 dead loop) and the answer could never be revised.
  // Remaining means "the question is askable at all" — the entry rule with the answered
  // check blanked — so a selection that never asks for extra time still redirects onward.
  // Forward flow is unaffected (getNextStep reads the entry rule); OQ2 logs the arm
  // asymmetry (the modal never re-asks).
  [BOOKING_FLOW_STEP_SLUGS.EXTRA_TIME]: (selection) =>
    showExtraTimeView(
      selection.services ?? [],
      resolvePickedEmployee(selection),
      EXTRA_TIME_REQUIREMENT.NotAnsweredYet,
    ),
};

/**
 * The R9 page guard, shared by every step page: returns the redirect target when `step`
 * must not render for `selection`, or `null` when it may. A missing prerequisite redirects
 * back to that prerequisite; a step whose own condition does not hold (not applicable, or
 * already answered) redirects onward via `getNextStep`.
 */
export const getStepGuardTarget = (
  step: BookingFlowStep,
  selection: BookingFlowStepSelection,
): BookingFlowGoToStepTarget | null => {
  for (const prerequisite of PREREQUISITE_STEPS) {
    if (prerequisite === step) break;
    if (STEP_CONDITIONS[prerequisite](selection)) {
      return { type: 'step', step: prerequisite };
    }
  }
  const mayRemain = REMAIN_CONDITIONS[step] ?? STEP_CONDITIONS[step];
  if (!mayRemain(selection)) {
    const target = getNextStep(step, selection);
    // A degenerate selection can make the fallback resolve to the step being guarded —
    // render instead of redirecting: a self-target can only ever loop.
    if (target.type === 'step' && target.step === step) {
      return null;
    }
    return target;
  }
  return null;
};
